"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  CHAT_READY_MESSAGE,
  PANEL_LANGUAGE_MESSAGE,
  PANEL_PAGE_MESSAGE,
  PANEL_VISIBLE_MESSAGE,
  PANEL_VISIBLE_REQUEST,
  VISIBLE_WAIT_MS,
  parseMessagePage,
  parseVisiblePage,
  type MessageContext,
  type VisiblePage,
} from "@shared/companion/message-context";
import { companionExtensionOrigins } from "@/lib/companion-extension";
import { isLocale } from "@/lib/i18n/locales";

/**
 * Where the person is as they write, for the messages they send
 * (@shared/companion/message-context). Null everywhere but the companion view.
 */
interface PanelValue {
  readonly context: MessageContext | null;
  /** The agent sees what is visible with every message
   *  (.plans/prd-companion-page-with-message.md): the chat says so above its
   *  input, and asks the panel for it as each message is sent. */
  readonly withMessages: boolean;
  /** The person's ✕ on that line: the next message goes without it. */
  readonly skipNext: boolean;
  readonly setSkipNext: (skip: boolean) => void;
  /** What is visible now, from the panel, or null when nothing came back
   *  within VISIBLE_WAIT_MS. */
  readonly requestVisible: () => Promise<VisiblePage | null>;
}

const NO_PANEL: PanelValue = {
  context: null,
  withMessages: false,
  skipNext: false,
  setSkipNext: () => undefined,
  requestVisible: () => Promise.resolve(null),
};

const PanelContextValue = createContext<PanelValue>(NO_PANEL);

export const useMessageContext = (): MessageContext | null => useContext(PanelContextValue).context;
export const usePanel = (): PanelValue => useContext(PanelContextValue);

/**
 * The context a message goes out with: the page, and, when the agent sees
 * the page with every message and the person did not skip it, what is visible
 * now. A message that waits in the outbox does not get it: by the time it
 * goes, the screen is another.
 */
export const contextForSend = async (panel: PanelValue): Promise<MessageContext | null> => {
  const { context } = panel;
  if (context === null || !panel.withMessages || panel.skipNext) return context;
  const visible = await panel.requestVisible();
  return visible === null ? context : { ...context, visible };
};

/**
 * The companion view's line to the browser side panel that frames it. The
 * panel says which page is in front of the person, whenever that changes and
 * only while this agent may know (sharing on, not paused, a page that may be
 * shared), and which language its settings chose. Heard only from our own
 * extension's origins, and only from the window that frames this one; the
 * runtime checks the page again before keeping it.
 */
export function PanelBridge({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const [context, setContext] = useState<MessageContext | null>(null);
  const [withMessages, setWithMessages] = useState(false);
  const [skipNext, setSkipNext] = useState(false);
  /** Answers the panel has not given yet, by request id. */
  const waiting = useRef(new Map<string, (visible: VisiblePage | null) => void>());
  /** The panel's origin, once it has spoken: where requests go. */
  const panelOrigin = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || window.parent === window) return;
    const origins = companionExtensionOrigins();
    const pending = waiting.current;
    const onMessage = (event: MessageEvent): void => {
      if (event.source !== window.parent || !origins.includes(event.origin)) return;
      panelOrigin.current = event.origin;
      const data: unknown = event.data;
      if (typeof data !== "object" || data === null || !("type" in data)) return;
      if (data.type === PANEL_PAGE_MESSAGE) {
        const page = "page" in data ? parseMessagePage(data.page) : null;
        setWithMessages(page !== null && "withMessages" in data && data.withMessages === true);
        // The same page again keeps the same object: the chat that reads it
        // re-renders only when the page actually changed.
        setContext((current) =>
          page === null ? null
          : current !== null && current.page.url === page.url && current.page.title === page.title && current.page.icon === page.icon ? current
          : { surface: "chrome", page });
      } else if (data.type === PANEL_VISIBLE_MESSAGE) {
        const id = "id" in data && typeof data.id === "string" ? data.id : null;
        const answer = id === null ? undefined : pending.get(id);
        if (id !== null && answer) {
          pending.delete(id);
          answer("visible" in data ? parseVisiblePage(data.visible) : null);
        }
      } else if (data.type === PANEL_LANGUAGE_MESSAGE) {
        const locale = "locale" in data && typeof data.locale === "string" ? data.locale : null;
        // Script in this frame cannot write the site's (Lax) locale cookies,
        // so the server sets them on its answer; then a full reload, as from
        // the site's own switcher. It also persists the account's language.
        if (isLocale(locale) && locale !== document.documentElement.lang) {
          void fetch("/api/ui-language", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ locale }),
          }).then(() => { window.location.reload(); }, () => undefined);
        }
      }
    };
    window.addEventListener("message", onMessage);
    // Ask for the current page (the panel's last one was sent before this
    // listener existed) and say which language this page renders in.
    window.parent.postMessage({ type: CHAT_READY_MESSAGE, locale: document.documentElement.lang }, "*");
    return () => {
      window.removeEventListener("message", onMessage);
      pending.forEach((answer) => { answer(null); });
      pending.clear();
    };
  }, [enabled]);

  const requestVisible = useCallback((): Promise<VisiblePage | null> => {
    const origin = panelOrigin.current;
    if (origin === null) return Promise.resolve(null);
    const id = crypto.randomUUID();
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        waiting.current.delete(id);
        resolve(null);
      }, VISIBLE_WAIT_MS);
      waiting.current.set(id, (visible) => {
        clearTimeout(timer);
        resolve(visible);
      });
      // Only to the panel that framed us: what comes back is the person's page.
      window.parent.postMessage({ type: PANEL_VISIBLE_REQUEST, id }, origin);
    });
  }, []);

  const value = useMemo<PanelValue>(
    () => (enabled ? { context, withMessages, skipNext, setSkipNext, requestVisible } : NO_PANEL),
    [enabled, context, withMessages, skipNext, requestVisible],
  );
  return <PanelContextValue.Provider value={value}>{children}</PanelContextValue.Provider>;
}
